import {
  ArrowRightIcon,
  CodeIcon,
  DatabaseIcon,
  ExternalLinkIcon,
  LockClosedIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import {
  getMarkyWebsiteUseCase,
  MarkyWebsiteAccent,
  MarkyWebsiteUseCase,
} from '@/constants/markyWebsiteUseCases';
import {
  translateMarkyWebsiteText,
  translateMarkyWebsiteUseCase,
} from '@/utils/markyWebsiteTranslation';

import MarkyWebsiteJsonLd from './MarkyWebsiteJsonLd';

const SITE_URL = 'https://easy-peasy.ai';
const HUB_PATH = '/marky/website-builder';

const accentStyles: Record<
  MarkyWebsiteAccent,
  {
    pale: string;
    border: string;
    text: string;
    button: string;
    icon: string;
  }
> = {
  olive: {
    pale: 'bg-[#edf1d3] dark:bg-[#18200f]',
    border: 'border-[#7f922d] dark:border-[#8fa34b]',
    text: 'text-[#59651f] dark:text-[#c4d578]',
    button:
      'bg-[#334015] hover:bg-[#46571d] dark:bg-[#506421] dark:hover:bg-[#607828]',
    icon: 'bg-[#dce69d] text-[#465515] dark:bg-[#2e3b1a] dark:text-[#d7e69a]',
  },
  brick: {
    pale: 'bg-[#f5ded7] dark:bg-[#251411]',
    border: 'border-[#a54835] dark:border-[#c76f5d]',
    text: 'text-[#8d3b2c] dark:text-[#f1a18f]',
    button:
      'bg-[#7e3125] hover:bg-[#9b4030] dark:bg-[#9b4030] dark:hover:bg-[#b94f3d]',
    icon: 'bg-[#efbbae] text-[#813426] dark:bg-[#3b1e18] dark:text-[#f4b3a3]',
  },
  forest: {
    pale: 'bg-[#dfeade] dark:bg-[#102016]',
    border: 'border-[#3f7049] dark:border-[#5d9a6c]',
    text: 'text-[#315d3a] dark:text-[#8bc49a]',
    button:
      'bg-[#234d31] hover:bg-[#326441] dark:bg-[#326441] dark:hover:bg-[#3f7a50]',
    icon: 'bg-[#b9d3bb] text-[#285737] dark:bg-[#1c3825] dark:text-[#a8d7b3]',
  },
  cobalt: {
    pale: 'bg-[#dfe7f9] dark:bg-[#111c34]',
    border: 'border-[#315fbd] dark:border-[#5c84d8]',
    text: 'text-[#2c54a7] dark:text-[#8faff0]',
    button:
      'bg-[#24468f] hover:bg-[#315db8] dark:bg-[#315db8] dark:hover:bg-[#3f70d5]',
    icon: 'bg-[#b9ccf2] text-[#24498f] dark:bg-[#1c315a] dark:text-[#a9c1f2]',
  },
  terracotta: {
    pale: 'bg-[#f2dfd0] dark:bg-[#261810]',
    border: 'border-[#aa5f3b] dark:border-[#ca7950]',
    text: 'text-[#925033] dark:text-[#e5a37f]',
    button:
      'bg-[#78402a] hover:bg-[#965136] dark:bg-[#965136] dark:hover:bg-[#b26140]',
    icon: 'bg-[#e7bea4] text-[#7a412b] dark:bg-[#42281c] dark:text-[#efb796]',
  },
  teal: {
    pale: 'bg-[#d9ece9] dark:bg-[#102321]',
    border: 'border-[#2b736d] dark:border-[#4f9d95]',
    text: 'text-[#24625d] dark:text-[#75c9c1]',
    button:
      'bg-[#194f4b] hover:bg-[#286963] dark:bg-[#286963] dark:hover:bg-[#33817a]',
    icon: 'bg-[#acd6d0] text-[#1f5b56] dark:bg-[#193b38] dark:text-[#92d5ce]',
  },
};

const stackItems = [
  {
    title: 'React + TypeScript',
    description: 'An editable frontend, not a proprietary page format.',
    Icon: CodeIcon,
  },
  {
    title: 'Postgres + typed APIs',
    description: 'Persistent records and protected server-side logic.',
    Icon: DatabaseIcon,
  },
  {
    title: 'Accounts + secrets',
    description: 'Authentication and protected credentials for integrations.',
    Icon: LockClosedIcon,
  },
];

type MarkyWebsiteUseCasePageProps = {
  useCase: MarkyWebsiteUseCase;
};

const MarkyWebsiteUseCasePage = ({
  useCase: sourceUseCase,
}: MarkyWebsiteUseCasePageProps) => {
  const { t, lang } = useTranslation('marky-websites');
  const tr = (value: string) => translateMarkyWebsiteText(t, value);
  const useCase = translateMarkyWebsiteUseCase(t, sourceUseCase);
  const isRtl = lang === 'ar';
  const accent = accentStyles[useCase.accent];
  const relatedUseCases = sourceUseCase.relatedSlugs
    .map(getMarkyWebsiteUseCase)
    .filter((item): item is MarkyWebsiteUseCase => Boolean(item))
    .map((item) => translateMarkyWebsiteUseCase(t, item));

  return (
    <main
      dir={isRtl ? 'rtl' : 'ltr'}
      className='bg-[#faf6ef] text-[#251d29] dark:bg-slate-950 dark:text-slate-50'
    >
      <Seo
        templateTitle={useCase.seoTitle}
        description={useCase.metaDescription}
        image={`${SITE_URL}${useCase.image}`}
        page='other'
        productName={useCase.name}
        productDescription={useCase.definition}
        productImage={[`${SITE_URL}${useCase.image}`]}
      />
      <MarkyWebsiteJsonLd useCase={useCase} />

      <div
        className={`border-b border-[#34243b]/10 dark:border-white/10 ${accent.pale}`}
      >
        <LandingPageNavigation skipUserContext />
        <section>
          <div className='mx-auto max-w-7xl px-4 pb-20 pt-8 sm:px-6 sm:pb-24 lg:px-8 lg:pb-28'>
            <nav
              aria-label='Breadcrumb'
              className='text-sm text-[#655b67] dark:text-slate-400'
            >
              <ol className='flex flex-wrap items-center gap-2'>
                <li>
                  <Link
                    href='/marky'
                    className='rounded-sm underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current'
                  >
                    {tr('Marky Agent')}
                  </Link>
                </li>
                <li aria-hidden='true'>/</li>
                <li>
                  <Link
                    href={HUB_PATH}
                    className='rounded-sm underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current'
                  >
                    {tr('Website builder')}
                  </Link>
                </li>
                <li aria-hidden='true'>/</li>
                <li
                  aria-current='page'
                  className='font-bold text-[#332938] dark:text-slate-100'
                >
                  {useCase.shortName}
                </li>
              </ol>
            </nav>

            <div className='mt-14 grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20'>
              <div>
                <p
                  className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
                >
                  {useCase.eyebrow}
                </p>
                <h1 className='mt-5 text-5xl font-black tracking-[-0.055em] sm:text-6xl lg:text-7xl'>
                  {useCase.heroTitle}
                </h1>
                <p className='mt-7 max-w-2xl text-xl leading-8 text-[#5e535f] dark:text-slate-300'>
                  {useCase.heroDescription}
                </p>
                <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                  <Link
                    href='/chat?mode=build-website'
                    className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 py-3 font-bold text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#3e3242] ${accent.button}`}
                  >
                    {tr('Build this with Marky')}
                    <ArrowRightIcon
                      className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`}
                      aria-hidden='true'
                    />
                  </Link>
                  <Link
                    href={HUB_PATH}
                    className='inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#332938]/25 bg-[#fffaf3]/75 px-6 py-3 font-bold transition-colors hover:border-[#332938]/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#3e3242] dark:border-white/20 dark:bg-slate-950/60 dark:hover:border-white/40 dark:focus-visible:outline-slate-200'
                  >
                    {tr('All website capabilities')}
                  </Link>
                </div>
              </div>

              <div className='relative aspect-[3/2] overflow-hidden rounded-[2rem] border border-[#332938]/15 bg-[#fffaf3] shadow-[0_28px_70px_rgba(45,30,50,0.17)] dark:border-white/10 dark:bg-slate-900 dark:shadow-none'>
                <Image
                  src={useCase.image}
                  alt={useCase.imageAlt}
                  fill
                  priority
                  sizes='(min-width: 1024px) 52vw, 94vw'
                  className='object-cover'
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className='px-4 py-16 sm:px-6 lg:px-8'>
        <div
          className={`mx-auto max-w-5xl py-2 ${accent.border} ${
            isRtl ? 'border-r-4 pr-6 sm:pr-9' : 'border-l-4 pl-6 sm:pl-9'
          }`}
        >
          <p
            className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
          >
            {tr('Direct answer')}
          </p>
          <h2 className='mt-3 text-3xl font-black tracking-[-0.025em] sm:text-4xl'>
            {t(
              'What is {{websiteType}}?',
              { websiteType: useCase.name },
              { default: `What is ${useCase.name}?` },
            )}
          </h2>
          <p className='mt-5 text-lg leading-8 text-[#5e535f] dark:text-slate-300'>
            {useCase.definition}
          </p>
        </div>
      </section>

      <section className='border-y border-[#34243b]/10 bg-[#fffdf8] px-4 py-20 dark:border-white/10 dark:bg-slate-900 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20'>
            <div>
              <p
                className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
              >
                {tr('Built around the job')}
              </p>
              <h2 className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'>
                {tr('What Marky can implement')}
              </h2>
              <p className='mt-5 text-lg leading-8 text-[#665b68] dark:text-slate-300'>
                {tr(
                  'These are functional product layers tied to the workflow—not decorative sections copied from a template.',
                )}
              </p>
            </div>

            <div className='grid border-t border-[#34243b]/20 dark:border-white/10 md:grid-cols-2'>
              {useCase.features.map((feature, index) => (
                <article
                  key={feature.title}
                  className={`border-b border-[#34243b]/20 py-7 dark:border-white/10 md:px-7 ${
                    index % 2 === 0 ? 'md:border-r' : ''
                  }`}
                >
                  <span
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-black ${accent.icon}`}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className='mt-5 text-xl font-black'>{feature.title}</h3>
                  <p className='mt-3 leading-7 text-[#665b68] dark:text-slate-300'>
                    {feature.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className='px-4 py-20 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto grid max-w-7xl gap-8 lg:grid-cols-2 lg:gap-12'>
          <div className={`rounded-[1.75rem] p-7 sm:p-9 ${accent.pale}`}>
            <p
              className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
            >
              {tr('Suggested data model')}
            </p>
            <h2 className='mt-4 text-3xl font-black tracking-[-0.03em]'>
              {tr('Records the first version may need')}
            </h2>
            <dl className='mt-8 divide-y divide-[#34243b]/15 border-y border-[#34243b]/15 dark:divide-white/10 dark:border-white/10'>
              {useCase.dataModel.map((item) => (
                <div
                  key={item.entity}
                  className='grid gap-2 py-5 sm:grid-cols-[0.65fr_1.35fr] sm:gap-5'
                >
                  <dt className='font-black'>{item.entity}</dt>
                  <dd className='leading-7 text-[#5e535f] dark:text-slate-300'>
                    {item.purpose}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className='rounded-[1.75rem] bg-[#211926] p-7 text-[#fffaf3] sm:p-9'>
            <p className='text-sm font-black uppercase tracking-[0.18em] text-[#cbb7d1]'>
              {tr('Example prompt')}
            </p>
            <h2 className='mt-4 text-3xl font-black tracking-[-0.03em]'>
              {tr('Give Marky a build brief, not a buzzword')}
            </h2>
            <blockquote
              className={`mt-8 border-[#b7d94c] text-lg leading-8 text-[#eee5f0] ${
                isRtl ? 'border-r-2 pr-5' : 'border-l-2 pl-5'
              }`}
            >
              “{useCase.buildBrief}”
            </blockquote>
            <Link
              href='/chat?mode=build-website'
              className='mt-8 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#fffaf3] px-5 py-2.5 font-bold text-[#2c2130] transition-colors hover:bg-[#e8dced] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white'
            >
              {tr('Use this as a starting point')}
              <ExternalLinkIcon className='h-4 w-4' aria-hidden='true' />
            </Link>
          </div>
        </div>
      </section>

      <section className='border-y border-[#34243b]/10 bg-[#eee7dd] px-4 py-20 dark:border-white/10 dark:bg-slate-900 sm:px-6 lg:px-8 lg:py-28'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20'>
            <div>
              <p
                className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
              >
                {tr('Practical build sequence')}
              </p>
              <h2 className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'>
                {tr('How to build it with Marky')}
              </h2>
            </div>
            <ol className='border-t border-[#34243b]/20 dark:border-white/10'>
              {useCase.buildSteps.map((step, index) => (
                <li
                  key={step.title}
                  className='grid gap-3 border-b border-[#34243b]/20 py-7 dark:border-white/10 sm:grid-cols-[56px_0.8fr_1.2fr] sm:gap-6'
                >
                  <span
                    className={`font-mono text-sm font-bold ${accent.text}`}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className='text-xl font-black'>{step.title}</h3>
                  <p className='leading-7 text-[#665b68] dark:text-slate-300'>
                    {step.description}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className='bg-[#211926] px-4 py-16 text-[#fffaf3] sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-7xl'>
          <div className='flex flex-col justify-between gap-5 border-b border-white/15 pb-8 sm:flex-row sm:items-end'>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.18em] text-[#cbb7d1]'>
                {tr('Shared production stack')}
              </p>
              <h2 className='mt-3 text-3xl font-black tracking-[-0.03em]'>
                {tr('Standard code, managed launch path')}
              </h2>
            </div>
            <Link
              href={HUB_PATH}
              className='inline-flex min-h-[44px] items-center gap-2 font-bold text-[#d4beff] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#d4beff]'
            >
              {tr('See the full capability map')}
              <ArrowRightIcon
                className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`}
                aria-hidden='true'
              />
            </Link>
          </div>
          <div className='mt-8 grid gap-8 md:grid-cols-3'>
            {stackItems.map(({ title, description, Icon }) => (
              <div key={title} className='grid grid-cols-[auto_1fr] gap-4'>
                <Icon className='h-6 w-6 text-[#b7d94c]' aria-hidden='true' />
                <div>
                  <h3 className='font-black'>{tr(title)}</h3>
                  <p className='mt-2 leading-7 text-[#cfc4d2]'>
                    {tr(description)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className='mt-9 border-t border-white/15 pt-7 text-sm leading-6 text-[#cfc4d2]'>
            {tr(
              'Built-in publishing targets Google Cloud Run. AWS services can be integrated through protected credentials, and the GitHub codebase can be adapted for an AWS deployment by a developer.',
            )}
          </p>
        </div>
      </section>

      <section
        aria-labelledby='use-case-faq'
        className='px-4 py-20 sm:px-6 lg:px-8 lg:py-28'
      >
        <div className='mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20'>
          <div>
            <p
              className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
            >
              {tr('Specific questions')}
            </p>
            <h2
              id='use-case-faq'
              className='mt-4 text-4xl font-black tracking-[-0.04em] sm:text-5xl'
            >
              {t(
                '{{websiteType}} FAQ',
                { websiteType: useCase.shortName },
                { default: `${useCase.shortName} FAQ` },
              )}
            </h2>
          </div>
          <div className='divide-y divide-[#34243b]/20 border-y border-[#34243b]/20 dark:divide-white/10 dark:border-white/10'>
            {useCase.faqs.map((faq) => (
              <details key={faq.question} className='group py-6'>
                <summary className='flex min-h-[44px] cursor-pointer list-none items-start justify-between gap-6 text-lg font-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#403342] [&::-webkit-details-marker]:hidden'>
                  {faq.question}
                  <span
                    aria-hidden='true'
                    className={`text-2xl font-normal group-open:rotate-45 ${accent.text}`}
                  >
                    +
                  </span>
                </summary>
                <p
                  className={`max-w-3xl pb-2 pt-3 leading-7 text-[#665b68] dark:text-slate-300 ${
                    isRtl ? 'pl-10' : 'pr-10'
                  }`}
                >
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className='border-t border-[#34243b]/10 bg-[#fffdf8] px-4 py-20 dark:border-white/10 dark:bg-slate-900 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-7xl'>
          <div className='flex flex-col justify-between gap-4 sm:flex-row sm:items-end'>
            <div>
              <p
                className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
              >
                {tr('Keep exploring')}
              </p>
              <h2 className='mt-3 text-3xl font-black tracking-[-0.03em]'>
                {tr('Related website types')}
              </h2>
            </div>
            <Link
              href={HUB_PATH}
              className='inline-flex min-h-[44px] items-center gap-2 font-bold underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#403342]'
            >
              {tr('View all six')}
              <ArrowRightIcon
                className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`}
                aria-hidden='true'
              />
            </Link>
          </div>
          <div className='mt-9 grid gap-5 md:grid-cols-3'>
            {relatedUseCases.map((related) => (
              <Link
                key={related.slug}
                href={`${HUB_PATH}/${related.slug}`}
                className='group overflow-hidden rounded-2xl border border-[#34243b]/15 bg-[#faf6ef] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#403342] dark:border-white/10 dark:bg-slate-800 dark:focus-visible:outline-slate-200'
              >
                <div className='relative aspect-[3/2] overflow-hidden border-b border-[#34243b]/10 dark:border-white/10'>
                  <Image
                    src={related.image}
                    alt={related.imageAlt}
                    fill
                    sizes='(min-width: 768px) 31vw, 94vw'
                    className='object-cover transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transition-none'
                  />
                </div>
                <span className='flex items-center justify-between gap-3 p-5 text-lg font-black'>
                  {related.name}
                  <span
                    aria-hidden='true'
                    className='transition-transform group-hover:translate-x-1 motion-reduce:transition-none'
                  >
                    {isRtl ? '←' : '→'}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className='px-4 py-20 sm:px-6 lg:px-8'>
        <div
          className={`mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 rounded-[2rem] p-8 sm:p-11 lg:flex-row lg:items-end ${accent.pale}`}
        >
          <div className='max-w-3xl'>
            <p
              className={`text-sm font-black uppercase tracking-[0.18em] ${accent.text}`}
            >
              {tr('Build the first working version')}
            </p>
            <h2 className='mt-3 text-3xl font-black tracking-[-0.035em] sm:text-4xl'>
              {tr(
                'Start with the workflow. Marky can help turn it into the app.',
              )}
            </h2>
          </div>
          <Link
            href='/chat?mode=build-website'
            className={`inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-full px-6 py-3 font-bold text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#3e3242] ${accent.button}`}
          >
            {tr('Build with Marky')}
            <ArrowRightIcon
              className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`}
              aria-hidden='true'
            />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default MarkyWebsiteUseCasePage;
